import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, MapPin, ArrowRight } from "lucide-react";
import { isNewActivity } from "@/lib/activityNewness";
import { optimizeImage } from "@/lib/imageUrl";

type LatestActivity = {
  id: string;
  name: string;
  created_at: string;
  venue_id: string | null;
  venue_name?: string | null;
  venue_district?: string | null;
  image_url?: string | null;
  category_name?: string | null;
};

const DISPLAY_LIMIT = 8;
const FETCH_LIMIT = 24;

export const LatestActivitiesSection = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<LatestActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const { data: rows, error } = await supabase
        .from("activities_public")
        .select("id, name, created_at, venue_id")
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(FETCH_LIMIT);

      if (error || !rows || cancelled) {
        if (!cancelled) {
          setActivities([]);
          setLoading(false);
        }
        return;
      }

      const ids = rows.map((r) => r.id);
      const venueIds = Array.from(
        new Set(rows.map((r) => r.venue_id).filter((v): v is string => !!v))
      );

      const [venuesRes, imagesRes, catRelRes] = await Promise.all([
        venueIds.length
          ? supabase.from("venues").select("id, name, district").in("id", venueIds)
          : Promise.resolve({ data: [] as any[] }),
        supabase
          .from("activity_images")
          .select("activity_id, image_url, display_order")
          .in("activity_id", ids)
          .order("display_order", { ascending: true }),
        supabase
          .from("activity_category_relations")
          .select("activity_id, category_id")
          .in("activity_id", ids),
      ]);

      const categoryIds = Array.from(
        new Set((catRelRes.data ?? []).map((r: any) => r.category_id))
      );
      const catRes = categoryIds.length
        ? await supabase
            .from("activity_categories")
            .select("id, name")
            .in("id", categoryIds)
        : { data: [] as any[] };

      if (cancelled) return;

      const venueMap = new Map(
        (venuesRes.data ?? []).map((v: any) => [v.id, v])
      );
      const imageMap = new Map<string, string>();
      for (const img of imagesRes.data ?? []) {
        if (!imageMap.has((img as any).activity_id)) {
          imageMap.set((img as any).activity_id, (img as any).image_url);
        }
      }
      const catNameMap = new Map(
        (catRes.data ?? []).map((c: any) => [c.id, c.name])
      );
      const activityCatMap = new Map<string, string>();
      for (const rel of catRelRes.data ?? []) {
        const r = rel as any;
        if (!activityCatMap.has(r.activity_id)) {
          const name = catNameMap.get(r.category_id);
          if (name) activityCatMap.set(r.activity_id, name);
        }
      }

      const enriched: LatestActivity[] = rows.map((r) => {
        const venue = r.venue_id ? venueMap.get(r.venue_id) : null;
        return {
          id: r.id,
          name: r.name,
          created_at: r.created_at,
          venue_id: r.venue_id,
          venue_name: venue?.name ?? null,
          venue_district: venue?.district ?? null,
          image_url: imageMap.get(r.id) ?? null,
          category_name: activityCatMap.get(r.id) ?? null,
        };
      });

      setActivities(enriched.filter((a) => !!a.image_url).slice(0, DISPLAY_LIMIT));
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && activities.length === 0) return null;

  const scrollToCalendar = () => {
    navigate("/activities");
  };


  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-light-fuschia/30 via-card to-primary/5 border-t border-border/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                Just Added
              </h2>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              The latest activities, classes & cafés added by the community
            </p>
          </div>
        </div>

        <div className="overflow-x-auto snap-x snap-mandatory scrollbar-none scroll-px-0">
          <div className="flex gap-4 pb-2">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="shrink-0 w-[78%] sm:w-[280px] bg-card rounded-2xl border border-border overflow-hidden snap-start"
                  >
                    <Skeleton className="w-full aspect-[4/3]" />
                    <div className="p-3 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))
              : activities.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/activity/${a.id}`)}
                    className="group shrink-0 w-[78%] sm:w-[280px] text-left bg-card rounded-2xl overflow-hidden border border-border hover:shadow-xl hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-300 snap-start"
                  >
                    <div className="relative w-full aspect-[4/3] overflow-hidden bg-muted">
                      {a.image_url ? (
                        <img
                          src={optimizeImage(a.image_url, { width: 600, height: 450, quality: 72, resize: "cover" })}
                          alt={a.name}
                          loading="lazy"
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                          <Sparkles className="h-10 w-10 text-primary/40" />
                        </div>
                      )}
                      {isNewActivity(a.created_at) && (
                        <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 shadow-md">
                          NEW
                        </Badge>
                      )}
                      {a.category_name && (
                        <span className="absolute bottom-2 left-2 text-[11px] font-semibold text-foreground bg-background/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                          {a.category_name}
                        </span>
                      )}
                    </div>
                    <div className="p-3 space-y-0.5 leading-[1.2]">
                      <h3 className="font-semibold text-base text-foreground line-clamp-1 leading-[1.18] group-hover:text-primary transition-colors">
                        {a.name}
                      </h3>
                      {(a.venue_name || a.venue_district) && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground leading-[1.2]">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            {a.venue_name}
                            {a.venue_name && a.venue_district ? " · " : ""}
                            {a.venue_district}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Button
            size="lg"
            onClick={scrollToCalendar}
            className="w-full sm:w-auto px-8 py-6 text-base font-semibold shadow-md hover:shadow-lg transition-all"
          >
            Explore activities
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LatestActivitiesSection;
